import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaBars, FaTimes } from "react-icons/fa";
import LogoDark from "../../assets/images/logo-dark.png";
import LogoLight from "../../assets/images/logo-light.png";
import ThemeToggleButton from "./ThemeToggleButton";
import "react-lazy-load-image-component/src/effects/blur.css";
import LogoComponent from "../Reusable/LogoComponent";
import NavbarProfilePopover from "../Header/NavBarProfilePopover";
import Avatar from "../Reusable/Avatar";
import avatarImg from "../../assets/images/avatar.png";
import CustomPopover from "../Reusable/Popover";
import { ReactComponent as ChatIcon } from "../../assets/icons/chat.svg";
import { useNavigate } from "react-router-dom";
import {
  logout,
  openSignInModal,
  setIntendedRoute,
} from "../../store/slices/authSlice";
import showToast from "../../utils/toastNotifications";
import { useTheme } from "../../context/ThemeContext";
import NavbarCompanySwitcher from "./NavbarCompanySwitcher";
import { Building2, Check } from "lucide-react";
import { useCompany } from "../../context/CompanyProvider";
import { getUnreadThreadCount } from "../../store/thunks/threadThunks";
import socket from "../../config/socket";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { currentCompany, companies, switchCompany, isMultiCompany } =
    useCompany();

  // Get user data from Redux store
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { unreadCount } = useSelector((state) => state.thread);
  const currentOpenThread = useSelector((state) => state.order.thread.data);

  // Check if user is on pending approval page
  const isOnPendingApprovalPage = location.pathname === "/pending-approval";

  // Check if user is on onboarding-related pages
  const isOnboardingPage = [
    "/",
    "/onboarding",
    "/reset-password",
    "/signin",
  ].includes(location.pathname);

  // State management
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProfilePopoverOpen, setIsProfilePopoverOpen] = useState(false);

  // Refs for click outside detection
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Handle scroll to add backdrop blur
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch unread count on mount and setup socket listeners
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUnreadThreadCount());

      const handleUnreadUpdate = () => {
        dispatch(getUnreadThreadCount());
      };

      const handleReceiveMessage = (message) => {
        // Check if the message is for the currently open thread
        const messageThreadId =
          message.thread?._id || message.threadId || message.thread;
        const currentThreadId = currentOpenThread?._id;

        // If the message is NOT for the currently open thread, refetch the count
        // If it IS for the current thread, the user is looking at it, so it's not "unread" in the UI context
        if (messageThreadId !== currentThreadId) {
          dispatch(getUnreadThreadCount());
        }
      };

      socket.on("unread-count-update", handleUnreadUpdate);
      socket.on("receive-message", handleReceiveMessage);

      return () => {
        socket.off("unread-count-update", handleUnreadUpdate);
        socket.off("receive-message", handleReceiveMessage);
      };
    }
  }, [dispatch, isAuthenticated, currentOpenThread]);

  useEffect(() => {
    console.log("Theme changed:", theme);
  }, [theme]);

  // Handle click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  // Close menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMenuOpen]);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMenuOpen]);

  // Navigation links configuration
  const NAV_LINKS = [
    {
      path: `${
        user?.hasActiveSubscription ? "/my-subscriptions" : "/services"
      }`,
      label: "Services",
      requiresAuth: true,
    },
    { path: "/orders", label: "Orders", requiresAuth: true },
    { path: "/user-profile", label: "Profile", requiresAuth: true },
  ];

  // Toggle handlers
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Generic handler for protected nav links (desktop)
  const handleProtectedLinkClick = (e, path) => {
    if (!isAuthenticated) {
      e.preventDefault();
      // Save intended route in Redux
      dispatch(setIntendedRoute(path));
      // Open sign-in modal
      dispatch(openSignInModal());
    }
  };

  // Generic handler for protected nav links (mobile)
  const handleMobileProtectedLinkClick = (e, path) => {
    if (!isAuthenticated) {
      e.preventDefault();
      // Save intended route in Redux
      dispatch(setIntendedRoute(path));
      // Open sign-in modal
      dispatch(openSignInModal());
    }
    // Close mobile menu
    setIsMenuOpen(false);
  };

  // Handle popover open/close
  const handleProfilePopoverOpenChange = (newOpen) => {
    setIsProfilePopoverOpen(newOpen);
  };

  // Close popover function
  const closeProfilePopover = () => {
    setIsProfilePopoverOpen(false);
  };

  // Handler for profile navigation
  const handleMyProfileClick = () => {
    closeProfilePopover();
    setTimeout(() => {
      navigate("/user-profile");
      setIsMenuOpen(false);
    }, 100);
  };

  // Handler for company profile navigation
  const handleCompanyProfileClick = () => {
    closeProfilePopover();
    setTimeout(() => {
      navigate("/company-profile");
      setIsMenuOpen(false);
    }, 100);
  };

  // Handler for settings navigation
  const handleSettingsClick = () => {
    closeProfilePopover();
    setTimeout(() => {
      navigate("/settings");
      setIsMenuOpen(false);
    }, 100);
  };

  const handleChatClick = () => {
    closeProfilePopover();
    setTimeout(() => {
      navigate("/full-chat-view");
      setIsMenuOpen(false);
    }, 100);
  };

  // Handle logout - navigate to onboarding without opening sign-in modal
  const handleLogout = () => {
    closeProfilePopover();
    setTimeout(() => {
      dispatch(logout());
      showToast("success", "Logged out successfully");
      navigate("/onboarding", { replace: true });
      setIsMenuOpen(false);
    }, 100);
  };

  // Get profile picture source - use uploaded image or fallback to avatar
  const getProfileImageSrc = () => {
    if (isAuthenticated && user?.profilePicture) {
      return user.profilePicture;
    }
    return avatarImg;
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return "My Account";
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return fullName || user.email || "My Account";
  };

  const profileImageSrc = getProfileImageSrc();

  return (
    <div className="fixed top-0 z-50 w-full">
      <nav
        className={`
          w-full max-w-[1440px] h-[67px] mx-auto px-8
          transition-all duration-300
          ${
            isScrolled
              ? "backdrop-blur-md bg-[var(--color-background-01)]/80"
              : "bg-transparent"
          }
        `}
      >
        {/* Bottom Border */}
        <div className="absolute bottom-0 left-1/2 w-full max-w-[calc(1440px-42px)] -translate-x-1/2 h-[1px] bg-[var(--color-stroke-01)]"></div>

        {/* Main Container */}
        <div className="relative flex items-center justify-between h-full">
          {/* Logo - Left End */}
          <div className="flex-shrink-0">
            <LogoComponent
              src={theme === "dark" ? LogoDark : LogoLight}
              alt="Logo"
              link="/"
              width={100}
              height={27}
              padding="0"
              gap={10}
              rounded="rounded-md"
            />
          </div>

          {/* Desktop Navigation Links - Center */}
          <div className="absolute items-center hidden gap-6 -translate-x-1/2 left-1/2 lg:flex">
            <Link
              to="/services"
              className="font-inter font-medium text-[16px] leading-[100%] capitalize text-[var(--color-content-01)] hover:text-[var(--color-primary)] transition-colors duration-200"
              onClick={(e) => handleProtectedLinkClick(e, "/services")}
            >
              Services
            </Link>

            <Link
              to="/orders"
              className="font-inter font-medium text-[16px] leading-[100%] capitalize text-[var(--color-content-01)] hover:text-[var(--color-primary)] transition-colors duration-200"
              onClick={(e) => handleProtectedLinkClick(e, "/orders")}
            >
              Orders
            </Link>

            <Link
              to="/user-profile"
              className="font-inter font-medium text-[16px] leading-[100%] capitalize text-[var(--color-content-01)] hover:text-[var(--color-primary)] transition-colors duration-200"
              onClick={(e) => handleProtectedLinkClick(e, "/user-profile")}
            >
              Profile
            </Link>
          </div>

          {/* Desktop Icons - Right End */}
          <div className="items-center flex-shrink-0 hidden gap-3 lg:flex">
            <ThemeToggleButton />

            {/* Sign In Button - Only when NOT authenticated */}
            {!isAuthenticated && (
              <button
                onClick={() => dispatch(openSignInModal())}
                className="w-[90px] h-8 flex items-center justify-center font-medium transition-all rounded-full hover:opacity-90"
                style={{
                  background: "var(--gradient-primary)",
                  color: "var(--color-content-08)",
                }}
              >
                Sign In
              </button>
            )}

            {/* Chat Icon - Only when authenticated */}
            {isAuthenticated && (
              <button
                onClick={handleChatClick}
                className="p-1.5 hover:opacity-80 transition-opacity relative"
              >
                <ChatIcon
                  width={24}
                  height={24}
                  className="text-[var(--color-content)]"
                />

                {/* Notification Badge */}
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 bg-[var(--color-primary)] text-white text-[8px] rounded-full">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
            )}

            {/* Profile Popover - Only when authenticated */}
            {isAuthenticated && (
              <CustomPopover
                key={user?.profilePicture}
                content={
                  <div className="navbar-popover-wrapper">
                    <NavbarProfilePopover
                      onMyProfileClick={handleMyProfileClick}
                      onCompanyProfileClick={handleCompanyProfileClick}
                      onSettingsClick={handleSettingsClick}
                      onLogoutClick={handleLogout}
                      showOnlyLogout={isOnPendingApprovalPage}
                    />
                  </div>
                }
                placement="bottomRight"
                trigger={["click"]}
                arrow={false}
                open={isProfilePopoverOpen}
                onOpenChange={handleProfilePopoverOpenChange}
              >
                <Avatar
                  src={profileImageSrc}
                  alt={getUserDisplayName()}
                  className="w-8 h-8 transition-opacity cursor-pointer hover:opacity-80"
                />
              </CustomPopover>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            ref={buttonRef}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            className="lg:hidden p-2 rounded-md transition-colors duration-200 text-[var(--color-content)] hover:bg-[var(--color-background-02)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          >
            {isMenuOpen ? (
              <FaTimes className="w-6 h-6" />
            ) : (
              <FaBars className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          ref={menuRef}
          style={{
            borderTop: "1px solid var(--color-stroke-01)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            background: isScrolled
              ? "var(--color-background-30)"
              : "transparent",
          }}
          className={`fixed left-0 right-0 shadow-lg transition-all duration-300 ease-in-out overflow-y-auto z-40 ${
            isScrolled ? "top-16" : "top-20"
          } ${
            isMenuOpen
              ? "max-h-[calc(100vh-4rem)] opacity-100"
              : "max-h-0 opacity-0 pointer-events-none"
          }`}
        >
          <div className="h-full px-6 py-6 space-y-6 overflow-y-auto">
            {/* Mobile Navigation Links */}
            <div className="space-y-4">
              {NAV_LINKS.map(({ path, label, requiresAuth }) => (
                <Link
                  key={path}
                  to={path}
                  className="block text-base font-medium text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors duration-200 py-2"
                  onClick={(e) => {
                    if (requiresAuth && !isAuthenticated) {
                      handleMobileProtectedLinkClick(e, path);
                    } else {
                      setIsMenuOpen(false);
                    }
                  }}
                >
                  {label}
                </Link>
              ))}
            </div>

            {/* Mobile Actions */}
            <div className="pt-4 border-t border-[var(--color-stroke-01)] space-y-4">
              {/* Theme Toggle - Always visible */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-[var(--color-content)]">
                  Theme
                </span>
                <ThemeToggleButton />
              </div>

              {/* Chat Icon - Only when authenticated and not on onboarding pages */}
              {isAuthenticated && (
                <button
                  onClick={handleChatClick}
                  className="flex items-center gap-3 w-full py-2 text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors"
                >
                  <div className="relative">
                    <ChatIcon width={24} height={24} className="text-current" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[var(--color-primary)] text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-medium">Chat</span>
                </button>
              )}

              {/* Simple Profile Links for Mobile - Only when authenticated and not on onboarding pages */}
              {isAuthenticated && (
                <div className="pt-4 border-t border-[var(--color-stroke-01)] space-y-2">
                  <button
                    onClick={handleMyProfileClick}
                    className="flex items-center gap-3 w-full py-2 text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors text-left"
                  >
                    <Avatar
                      src={profileImageSrc}
                      alt={getUserDisplayName()}
                      className="w-6 h-6 flex-shrink-0"
                    />
                    <span className="text-sm font-medium">My Profile</span>
                  </button>
                  <button
                    onClick={handleCompanyProfileClick}
                    className="flex items-center gap-3 w-full py-2 text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors text-left"
                  >
                    <span className="text-sm font-medium">Company Profile</span>
                  </button>
                  <button
                    onClick={handleSettingsClick}
                    className="flex items-center gap-3 w-full py-2 text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors text-left"
                  >
                    <span className="text-sm font-medium">Settings</span>
                  </button>
                  <div className="border-t border-[var(--color-stroke-01)] pt-2">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full py-2 text-[var(--color-error)] hover:text-[var(--color-error-dark)] transition-colors text-left"
                    >
                      <span className="text-sm font-medium">Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;

// return (
//   <div className="fixed top-0 z-50 w-full">
//     <nav
//       className={`
//         w-full max-w-[1440px] h-[67px] mx-auto px-8
//         transition-all duration-300
//         ${
//           isScrolled
//             ? "backdrop-blur-md bg-[var(--color-background-01)]/80"
//             : "bg-transparent"
//         }
//       `}
//     >
//       {/* Bottom Border */}
//       <div className="absolute bottom-0 left-1/2 w-full max-w-[calc(1440px-42px)] -translate-x-1/2 h-[1px] bg-[var(--color-stroke-01)]"></div>

//       {/* Main Container */}
//       <div className="relative flex items-center justify-between h-full">
//         {/* Logo - Left End */}
//         <div className="flex-shrink-0">
//           <LogoComponent
//             src={theme === "dark" ? LogoDark : LogoLight}
//             alt="Logo"
//             link="/"
//             width={100}
//             height={27}
//             padding="0"
//             gap={10}
//             rounded="rounded-md"
//           />
//         </div>

//         {/* Desktop Navigation Links - Center */}
//         <div className="absolute items-center hidden gap-6 -translate-x-1/2 left-1/2 lg:flex">
//           <Link
//             to="/services"
//             className="font-inter font-medium text-[16px] leading-[100%] capitalize text-[var(--color-content-01)] hover:text-[var(--color-primary)] transition-colors duration-200"
//             onClick={(e) => handleProtectedLinkClick(e, "/services")}
//           >
//             Services
//           </Link>

//           <Link
//             to="/orders"
//             className="font-inter font-medium text-[16px] leading-[100%] capitalize text-[var(--color-content-01)] hover:text-[var(--color-primary)] transition-colors duration-200"
//             onClick={(e) => handleProtectedLinkClick(e, "/orders")}
//           >
//             Orders
//           </Link>

//           <Link
//             to="/user-profile"
//             className="font-inter font-medium text-[16px] leading-[100%] capitalize text-[var(--color-content-01)] hover:text-[var(--color-primary)] transition-colors duration-200"
//             onClick={(e) => handleProtectedLinkClick(e, "/user-profile")}
//           >
//             Profile
//           </Link>
//         </div>

//         {/* Desktop Icons - Right End */}
//         <div className="items-center flex-shrink-0 hidden gap-3 lg:flex">
//           <ThemeToggleButton />

//           {/* Sign In Button - Only when NOT authenticated */}
//           {!isAuthenticated && (
//             <button
//               onClick={() => dispatch(openSignInModal())}
//               className="w-[90px] h-8 flex items-center justify-center font-medium transition-all rounded-full hover:opacity-90"
//               style={{
//                 background: "var(--gradient-primary)",
//                 color: "var(--color-content-08)",
//               }}
//             >
//               Sign In
//             </button>
//           )}

//           {/* Chat Icon and Company Switcher - Only when authenticated */}
//           {isAuthenticated && (
//             <>
//               <button
//                 onClick={handleChatClick}
//                 className="p-1.5 hover:opacity-80 transition-opacity"
//               >
//                 <ChatIcon
//                   width={24}
//                   height={24}
//                   className="text-[var(--color-content)]"
//                 />
//               </button>

//               {/* Company Switcher */}
//               {/* <NavbarCompanySwitcher /> */}

//               {/* Profile Popover */}
//               <CustomPopover
//                 key={user?.profilePicture}
//                 content={
//                   <div className="navbar-popover-wrapper">
//                     <NavbarProfilePopover
//                       onMyProfileClick={handleMyProfileClick}
//                       onCompanyProfileClick={handleCompanyProfileClick}
//                       onSettingsClick={handleSettingsClick}
//                       onLogoutClick={handleLogout}
//                       showOnlyLogout={isOnPendingApprovalPage}
//                     />
//                   </div>
//                 }
//                 placement="bottomRight"
//                 trigger={["click"]}
//                 arrow={false}
//                 open={isProfilePopoverOpen}
//                 onOpenChange={handleProfilePopoverOpenChange}
//               >
//                 <Avatar
//                   src={user?.profilePicture || avatarImg}
//                   alt={`${user?.firstName || "User"} ${user?.lastName || ""}`}
//                   className="w-8 h-8 transition-opacity cursor-pointer hover:opacity-80"
//                 />
//               </CustomPopover>
//             </>
//           )}
//         </div>

//         {/* Mobile Menu Button */}
//         <button
//           ref={buttonRef}
//           onClick={toggleMenu}
//           aria-label="Toggle menu"
//           aria-expanded={isMenuOpen}
//           className="lg:hidden p-2 rounded-md transition-colors duration-200 text-[var(--color-content)] hover:bg-[var(--color-background-02)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
//         >
//           {isMenuOpen ? (
//             <FaTimes className="w-6 h-6" />
//           ) : (
//             <FaBars className="w-6 h-6" />
//           )}
//         </button>
//       </div>

//       {/* Mobile Navigation Menu */}
//       <div
//         ref={menuRef}
//         style={{
//           borderTop: "1px solid var(--color-stroke-01)",
//           backdropFilter: "blur(12px)",
//           WebkitBackdropFilter: "blur(12px)",
//           background: isScrolled
//             ? "var(--color-background-30)"
//             : "transparent",
//         }}
//         className={`fixed left-0 right-0 shadow-lg transition-all duration-300 ease-in-out overflow-y-auto z-40 ${
//           isScrolled ? "top-16" : "top-20"
//         } ${
//           isMenuOpen
//             ? "max-h-[calc(100vh-4rem)] opacity-100"
//             : "max-h-0 opacity-0 pointer-events-none"
//         }`}
//       >
//         {/* Mobile Navigation Menu */}
//         <div
//           ref={menuRef}
//           style={{
//             borderTop: "1px solid var(--color-stroke-01)",
//             backdropFilter: "blur(12px)",
//             WebkitBackdropFilter: "blur(12px)",
//             background: isScrolled
//               ? "var(--color-background-30)"
//               : "transparent",
//           }}
//           className={`fixed left-0 right-0 shadow-lg transition-all duration-300 ease-in-out overflow-y-auto z-40 ${
//             isScrolled ? "top-16" : "top-20"
//           } ${
//             isMenuOpen
//               ? "max-h-[calc(100vh-4rem)] opacity-100"
//               : "max-h-0 opacity-0 pointer-events-none"
//           }`}
//         >
//           <div className="h-full px-6 py-6 space-y-6 overflow-y-auto">
//             {/* Mobile Navigation Links */}
//             <div className="space-y-4">
//               {NAV_LINKS.map(({ path, label, requiresAuth }) => (
//                 <Link
//                   key={path}
//                   to={path}
//                   className="block text-base font-medium text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors duration-200 py-2"
//                   onClick={(e) => {
//                     if (requiresAuth && !isAuthenticated) {
//                       handleMobileProtectedLinkClick(e, path);
//                     } else {
//                       setIsMenuOpen(false);
//                     }
//                   }}
//                 >
//                   {label}
//                 </Link>
//               ))}
//             </div>

//             {/* Mobile Actions */}
//             <div className="pt-4 border-t border-[var(--color-stroke-01)] space-y-4">
//               {/* Mobile Company Switcher - Only when authenticated */}
//               {isAuthenticated && (
//                 <div className="py-2">
//                   <div className="flex items-center justify-between mb-2">
//                     <span className="text-sm font-medium text-[var(--color-content)]">
//                       Company
//                     </span>
//                     <Building2
//                       size={16}
//                       className="text-[var(--color-content-02)]"
//                     />
//                   </div>
//                   <div className="space-y-2">
//                     {companies?.map((company) => (
//                       <button
//                         key={company.id}
//                         onClick={() => {
//                           switchCompany(company.id);
//                           setIsMenuOpen(false);
//                         }}
//                         className={`flex items-center justify-between w-full p-3 rounded-lg transition-colors ${
//                           currentCompany?.id === company.id
//                             ? "bg-[var(--color-background-02)] border border-[var(--color-primary)]"
//                             : "bg-[var(--color-background-02)] hover:bg-[var(--color-background-04)]"
//                         }`}
//                       >
//                         <div className="text-left">
//                           <p className="text-sm font-medium text-[var(--color-content)]">
//                             {company.name}
//                           </p>
//                           <p className="text-xs text-[var(--color-content-02)]">
//                             {company.role}
//                           </p>
//                         </div>
//                         {currentCompany?.id === company.id && (
//                           <Check
//                             size={16}
//                             className="text-[var(--color-primary)]"
//                           />
//                         )}
//                       </button>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               {/* Theme Toggle - Always visible */}
//               <div className="flex items-center justify-between py-2">
//                 <span className="text-sm font-medium text-[var(--color-content)]">
//                   Theme
//                 </span>
//                 <ThemeToggleButton />
//               </div>

//               {/* Chat Icon - Only when authenticated */}
//               {isAuthenticated && (
//                 <button
//                   onClick={handleChatClick}
//                   className="flex items-center gap-3 w-full py-2 text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors"
//                 >
//                   <ChatIcon width={24} height={24} className="text-current" />
//                   <span className="text-sm font-medium">Chat</span>
//                 </button>
//               )}

//               {/* Simple Profile Links for Mobile - Only when authenticated */}
//               {isAuthenticated && (
//                 <div className="pt-4 border-t border-[var(--color-stroke-01)] space-y-2">
//                   <button
//                     onClick={handleMyProfileClick}
//                     className="flex items-center gap-3 w-full py-2 text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors text-left"
//                   >
//                     <span className="text-sm font-medium">My Profile</span>
//                   </button>
//                   <button
//                     onClick={handleCompanyProfileClick}
//                     className="flex items-center gap-3 w-full py-2 text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors text-left"
//                   >
//                     <span className="text-sm font-medium">
//                       Company Profile
//                     </span>
//                   </button>
//                   <button
//                     onClick={handleSettingsClick}
//                     className="flex items-center gap-3 w-full py-2 text-[var(--color-content)] hover:text-[var(--color-primary)] transition-colors text-left"
//                   >
//                     <span className="text-sm font-medium">Settings</span>
//                   </button>
//                   <div className="border-t border-[var(--color-stroke-01)] pt-2">
//                     <button
//                       onClick={handleLogout}
//                       className="flex items-center gap-3 w-full py-2 text-[var(--color-error)] hover:text-[var(--color-error-dark)] transition-colors text-left"
//                     >
//                       <span className="text-sm font-medium">Logout</span>
//                     </button>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </nav>
//   </div>
// );
